import React, { useCallback, useEffect, useState } from 'react'
import {
  CAlert,
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CCol,
  CDropdown,
  CDropdownHeader,
  CDropdownItem,
  CDropdownMenu,
  CDropdownToggle,
  CFormCheck,
  CFormSwitch,
  CRow,
  CSmartTable,
} from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import {
  cilCalendar,
  cilCalendarCheck,
  cilCloudUpload,
  cilDescription,
  cilPencil,
  cilPlus,
  cilSpreadsheet,
  cilTrash,
  cilUserPlus,
} from '@coreui/icons'

import moment from 'moment'
import Bootbox from 'bootbox-react'
import { useNavigate } from 'react-router-dom'
import ToastComponent from 'src/components/common/TaostComponent.js'
import FixtureService from 'src/service/FixtureService'
import CommonService from 'src/service/CommonService'
import Pusher from 'pusher-js'
import Loader from 'src/components/Loader'
import FixtureSquadTable from './FixtureSquadTable.js'
import AddForm from './AddForm'
import AddFormForLeagueIndoorFixture from './AddFormForLeagueIndoorFixture'
import EditForm from './EditForm'
import Notify from '../Notify'
import PreviewRoundTable from 'src/views/Round/PreviewRoundTable'

const STATUS_BADGES = {
  0: { color: 'warning', label: 'Not Started' },
  1: { color: 'info', label: 'In-Progress' },
  2: { color: 'success', label: 'Completed' },
  3: { color: 'danger', label: 'Failed' },
  4: { color: 'secondary', label: 'In-Queue' },
}

const ManageFixture = (props) => {
  const [loading, setLoading] = useState(true)
  const [loader, setLoader] = useState(true)
  const [loaderText, setLoaderText] = useState(true)

  const [activePage, setActivePage] = useState(1)
  const [columnFilter, setColumnFilter] = useState([])
  const [columnSorter, setColumnSorter] = useState(null)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [users, setUsers] = useState({})
  const [squadFixtureIds, setSquadFixtureIds] = useState([])
  const [inProgressFixture, setInProgressFixture] = useState([])
  const navigate = useNavigate()
  const [gameType, setGameType] = useState()

  // Scheduling controls that used to live on the separate "All Fixtures" tab.
  const [displayDetails, setDisplayDetails] = useState([])
  const [addFixtureVisible, setAddFixtureVisible] = useState(false)
  const [editFixtureId, setEditFixtureId] = useState(null)
  const [roundPreviewVisible, setRoundPreviewVisible] = useState(false)

  useEffect(() => {
    CommonService.getGameType().then((res) => {
      if (res.status === 200) {
        setGameType(res.data)
      }
    })
  }, [])

  useEffect(() => {
    FixtureService.getActivatedDisplay().then((res) => {
      if (res.status === 200) {
        setDisplayDetails(res.data)
      }
    })
  }, [])

  // gameType 1 fixtures only carry a single team, so the away-team column is dropped for them.
  const isSingleTeam = gameType === 1
  // gameType 1 and 4 score one team at a time instead of picking a side from the fixture.
  const isSingleTeamScoring = gameType === 1 || gameType === 4

  // Delete Fixture code started here
  const [selectedCheckboxes, setSelectedCheckboxes] = useState([])

  const toggleAllCheckboxes = () => {
    const allIds = squadFixtureIds.map((user) => user.id)
    setSelectedCheckboxes((prevSelected) => (prevSelected.length === allIds.length ? [] : allIds))
  }

  const columns = [
    {
      key: 'delete_fixture',
      label: (
        <CFormCheck
          id="select_all_fixtures"
          aria-label="Select all fixtures without a squad"
          checked={
            selectedCheckboxes.length === squadFixtureIds.length && squadFixtureIds.length > 0
          }
          onChange={toggleAllCheckboxes}
        />
      ),
      filter: false,
      sorter: false,
    },
    {
      label: 'FID',
      key: 'id',
      _style: { width: '10%' },
    },
    {
      key: 'round',
    },
    {
      label: isSingleTeam ? 'Team Name' : 'Home Team',
      key: 'team_name',
    },
    ...(isSingleTeam ? [] : [{ label: 'Away Team', key: 'team2_name' }]),
    { label: 'Match Type', key: 'match_type', filter: false, sorter: false },
    { label: 'Start Date', filter: false, key: 'start_date' },
    { label: 'Start Time', filter: false, key: 'start_time' },
    { label: 'Status', filter: false, key: 'status' },
    { key: 'display', label: 'Display', filter: false, sorter: false },
    {
      key: 'show_details',
      label: 'Actions',
      _style: { width: '1%' },
      filter: false,
      sorter: false,
    },
  ]

  const fetchUsers = useCallback(async () => {
    setLoader(false)
    const offset = itemsPerPage * activePage - itemsPerPage
    let params = new URLSearchParams()
    Object.keys(columnFilter).forEach((key) => {
      params.append(key, columnFilter[key])
    })
    columnSorter &&
      columnSorter.column !== undefined &&
      params.append('sort', `${columnSorter.column}%${columnSorter.state}`)

    try {
      const result = await FixtureService.getFixture(offset, itemsPerPage, activePage, params)
      const squadResult = await FixtureService.getSquadFixture()
      const inProgressFixtureResult = await FixtureService.getInProgressFixture()
      setUsers(result.data)
      setSquadFixtureIds(squadResult.data ?? [])
      setInProgressFixture(inProgressFixtureResult.data)
      if (inProgressFixtureResult.data && inProgressFixtureResult.data != null) {
        setLoader(true)
        setLoaderText(inProgressFixtureResult?.message)
      }
    } catch (error) {
      console.error('Error fetching users:', error)
    } finally {
      setLoading(false)
    }
  }, [activePage, itemsPerPage, columnFilter, columnSorter])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  // AddForm/EditForm call back with the listing arguments the old tab used, so the
  // signature is kept even though this screen reads its paging from state.
  const refetchFixtures = useCallback(() => {
    fetchUsers()
  }, [fetchUsers])

  const toggleCheckbox = (id) => {
    setSelectedCheckboxes((prevSelected) => {
      if (prevSelected.includes(id)) {
        return prevSelected.filter((selectedId) => selectedId !== id)
      } else {
        return [...prevSelected, id]
      }
    })
  }

  const handleChangeStatus = (itemId) => {
    const position = displayDetails.indexOf(itemId)
    let newDetails = displayDetails.slice()
    if (position !== -1) {
      newDetails.splice(position, 1)
    } else {
      newDetails = [...displayDetails, itemId]
    }
    setDisplayDetails(newDetails)

    FixtureService.changeDisplayStatus({ fixtureId: itemId }).then((res) => {
      if (res.status === 200) {
        ToastComponent(res.message, 'success')
      }
    })
  }

  // Single-row delete confirm box
  const [handleYes, setHandleYes] = useState(false)
  const [handleNo, setHandleNo] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [tableId, setTableId] = useState(false)

  const deleteFixture = (id) => {
    FixtureService.deleteFixture({ id }).then((res) => {
      if (res.status === 200) {
        ToastComponent(res.message, 'success')
        fetchUsers()
      }
    })
  }

  const handleCancel = () => setShowConfirm(false)

  const handleConfirm = () => {
    deleteFixture(tableId)
    return setShowConfirm(false)
  }

  // A fixture that is already referenced elsewhere (scores, squads) cannot be removed.
  const areYouSureModal = (id) => {
    const data = { id, type: 'fixture' }
    CommonService.checkItemExists(data).then((res) => {
      if (res.status === 200) {
        if (res.data) {
          ToastComponent(res.message, 'error')
        } else {
          setShowConfirm(true)
          setTableId(id)
        }
      }
    })
  }

  // Delete All confirm box
  const [showDeleteAllConfirm, showShowDeleteAllConfirm] = useState(false)
  const showDeleteAllCofirmBox = () => {
    showShowDeleteAllConfirm(true)
  }
  const handleDeleteAllConfirm = () => {
    handleDeleteAllFixture()
    return showShowDeleteAllConfirm(false)
  }
  const handleDeleteAllCancel = () => {
    return showShowDeleteAllConfirm(false)
  }
  const handleDeleteAllFixture = () => {
    const data = {
      fixturesIds: selectedCheckboxes, // Include the array of IDs here
    }
    FixtureService.deleteAllFixture(data).then((res) => {
      if (res.status === 200) {
        ToastComponent(res.message, 'success')
        setLoading(false)
        setSelectedCheckboxes([])
        fetchUsers()
      }
    })
  }
  useEffect(() => {
    const pusher = new Pusher('2604209a1a0fdaa07dfc', {
      cluster: 'ap4',
    })
    const channel = pusher.subscribe('test-channel')
    channel.bind('test-event', (data) => {
      ToastComponent(data.message, 'red_error')
      fetchUsers()
    })

    const fixtureCompletionChannel = pusher.subscribe('fixture-completion-channel')
    fixtureCompletionChannel.bind('fixture-completion-event', (data) => {
      ToastComponent(data.message, 'success')
      fetchUsers()
    })
    return () => {
      // Unbind events and unsubscribe from channels
      channel.unbind_all()
      channel.unsubscribe()
      fixtureCompletionChannel.unbind_all()
      fixtureCompletionChannel.unsubscribe()
    }
  }, [fetchUsers])

  const redirectPage = (id, teamId) => {
    window.location.href = `/scorecard/${id}/${teamId}`
  }

  // show squad players
  const [teamId, setTeamId] = useState(0)
  const [teamName, setTeamName] = useState(0)
  const [fixtureId, setFixtureId] = useState(0)
  const [showSquadModal, setShowSquadModal] = useState(false)
  const showSquadPlayer = (fixtureId, teamId, teamName) => {
    setTeamId(teamId)
    setFixtureId(fixtureId)
    setShowSquadModal(true)
    setTeamName(teamName)
  }
  // Picking or dropping a player changes which fixtures still count as squad-less, so the
  // listing (and its delete checkboxes) has to be re-read when the modal reports a change.
  const closeSquadModal = (squadChanged) => {
    setShowSquadModal(false)
    if (squadChanged) {
      fetchUsers()
    }
  }

  // Same number the squad modal shows under "In Squad", served with the listing so the
  // page does not have to ask the team-player endpoint once per row.
  const squadCountPill = (count) => {
    const total = count ?? 0
    return (
      <span
        className={`ge-squad-count${total > 0 ? '' : ' ge-squad-count--empty'}`}
        title={`${total} ${total === 1 ? 'player' : 'players'} in squad`}
      >
        {total}
      </span>
    )
  }

  const selectedCount = selectedCheckboxes.length

  return loader ? (
    <Loader loaderText={loaderText} />
  ) : (
    <>
      <div className="d-flex flex-wrap justify-content-end gap-2 mb-3">
        <div className="d-flex flex-wrap gap-2">
          <CButton color="secondary" variant="outline" onClick={() => navigate('/fixture-import')}>
            <CIcon icon={cilCloudUpload} className="me-1" />
            Import Fixtures
          </CButton>
          <CButton color="secondary" variant="outline" onClick={() => setRoundPreviewVisible(true)}>
            <CIcon icon={cilCalendar} className="me-1" />
            Preview Rounds
          </CButton>
          {gameType && (
            <CButton color="primary" onClick={() => setAddFixtureVisible(true)}>
              <CIcon icon={cilPlus} className="me-1" />
              Add Fixture
            </CButton>
          )}
        </div>
      </div>

      {gameType == 4 && (
        <CAlert color="danger">Restriction: You can create a maximum of 1 Fixture only</CAlert>
      )}

      <CRow className="g-3 mb-3">
        <CCol sm={6} lg={4}>
          <CCard className="h-100">
            <CCardBody className="d-flex justify-content-between align-items-center">
              <div>
                <div className="text-medium-emphasis small">Total Fixtures</div>
                <div className="fs-4 fw-semibold">{users?.total ?? 0}</div>
              </div>
              <div
                className="d-flex align-items-center justify-content-center rounded-circle bg-primary-subtle text-primary"
                style={{ width: 42, height: 42 }}
              >
                <CIcon icon={cilCalendarCheck} size="lg" />
              </div>
            </CCardBody>
          </CCard>
        </CCol>
        <CCol sm={6} lg={4}>
          <CCard className="h-100">
            <CCardBody className="d-flex justify-content-between align-items-center">
              <div>
                <div className="text-medium-emphasis small">Awaiting Squad</div>
                <div className="fs-4 fw-semibold">{squadFixtureIds?.length ?? 0}</div>
              </div>
              <div
                className="d-flex align-items-center justify-content-center rounded-circle bg-warning-subtle text-warning"
                style={{ width: 42, height: 42 }}
              >
                <CIcon icon={cilUserPlus} size="lg" />
              </div>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <CCard>
        <CCardBody>
          {selectedCount > 0 && (
            <div
              className="d-flex flex-wrap align-items-center gap-2 mb-3 px-3 py-2 bg-primary-subtle"
              style={{ borderRadius: '0.75rem' }}
            >
              <span className="small fw-semibold text-primary">
                {selectedCount} {selectedCount === 1 ? 'fixture' : 'fixtures'} selected
              </span>
              <div className="d-flex flex-wrap align-items-center gap-2 ms-auto">
                <CButton color="danger" size="sm" onClick={showDeleteAllCofirmBox}>
                  <CIcon icon={cilTrash} className="me-1" />
                  Delete Selected
                </CButton>
                <CButton
                  color="secondary"
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedCheckboxes([])}
                >
                  Clear
                </CButton>
              </div>
            </div>
          )}

          <div className="ge-datatable ge-datatable--static">
            <CSmartTable
              columns={columns}
              noItemsLabel="No fixtures found"
              columnFilter={{
                external: true,
              }}
              columnSorter={{
                external: true,
              }}
              scopedColumns={{
                delete_fixture: (item) => {
                  // Only fixtures that have no squad yet can be deleted.
                  const fixtureIdExists = squadFixtureIds.some((fixture) => fixture.id === item.id)
                  return (
                    <td>
                      {fixtureIdExists && (
                        <CFormCheck
                          id={`delete_fixture_${item.id}`}
                          aria-label={`Select fixture ${item.id}`}
                          value={item.id}
                          checked={selectedCheckboxes.includes(item.id)}
                          onChange={() => toggleCheckbox(item.id)}
                        />
                      )}
                    </td>
                  )
                },
                id: (item) => (
                  <td>
                    <span
                      className="fw-semibold text-primary"
                      role="button"
                      title="Edit fixture"
                      onClick={() => setEditFixtureId(item.id)}
                    >
                      {item.id}
                    </span>
                  </td>
                ),
                round: (item) => <td>{item.round ?? '-'}</td>,
                team_name: (item) => (
                  <td>
                    <span
                      className="fw-semibold text-primary"
                      role="button"
                      title="View squad"
                      onClick={() => showSquadPlayer(item.id, item.team, item.team_name)}
                    >
                      {item.team_name ?? '-'}
                    </span>
                    {squadCountPill(item.team_squad_count)}
                  </td>
                ),
                team2_name: (item) => (
                  <td>
                    <span
                      className="fw-semibold text-primary"
                      role="button"
                      title="View squad"
                      onClick={() => showSquadPlayer(item.id, item.team2, item.team2_name)}
                    >
                      {item.team2_name ?? '-'}
                    </span>
                    {squadCountPill(item.team2_squad_count)}
                  </td>
                ),
                match_type: (item) => <td>{item.match_type ?? '-'}</td>,
                status: (item) => {
                  const badge = STATUS_BADGES[item.status]
                  return (
                    <td>
                      {badge && (
                        <CBadge color={badge.color} shape="rounded-pill" className="px-2 py-1">
                          {badge.label}
                        </CBadge>
                      )}
                    </td>
                  )
                },
                grade: (item) => <td>{item.grade ?? ''}</td>,
                start_date: (item) => <td>{moment(item.start_date).format('D.MM.YYYY')}</td>,
                start_time: (item) => (
                  <td>{moment(item.start_time, ['HH:mm']).format('h:mm A')}</td>
                ),
                display: (item) => (
                  <td className="py-2">
                    <CFormSwitch
                      label=""
                      id={`display_id${item.id}`}
                      name={`display_name${item.id}`}
                      value={item.id}
                      checked={!displayDetails.includes(item.id)}
                      onChange={() => handleChangeStatus(item.id)}
                    />
                  </td>
                ),
                // One icon per action instead of a three-dot menu. Two-team game types
                // still need a side picked for squad/scores, so those two icons open a
                // small team chooser; single-team game types go straight through.
                show_details: (item) => (
                  <td className="py-2">
                    <div className="d-flex align-items-center gap-1 flex-nowrap">
                      <CButton
                        color="secondary"
                        variant="ghost"
                        size="sm"
                        className="p-1"
                        title="Edit Fixture"
                        aria-label={`Edit fixture ${item.id}`}
                        onClick={() => setEditFixtureId(item.id)}
                      >
                        <CIcon icon={cilPencil} />
                      </CButton>

                      <CButton
                        color="danger"
                        variant="ghost"
                        size="sm"
                        className="p-1"
                        title="Delete Fixture"
                        aria-label={`Delete fixture ${item.id}`}
                        onClick={() => areYouSureModal(item.id)}
                      >
                        <CIcon icon={cilTrash} />
                      </CButton>

                      {isSingleTeamScoring ? (
                        <CButton
                          color="secondary"
                          variant="ghost"
                          size="sm"
                          className="p-1"
                          title="Create Squad"
                          aria-label={`Create squad for fixture ${item.id}`}
                          onClick={() =>
                            navigate(`/manage-scores/team-players/${item.id}/${item.team}`)
                          }
                        >
                          <CIcon icon={cilUserPlus} />
                        </CButton>
                      ) : (
                        <CDropdown alignment="end" placement="bottom-end" portal>
                          <CDropdownToggle
                            color="secondary"
                            variant="ghost"
                            size="sm"
                            caret={false}
                            className="p-1 ge-row-actions-toggle"
                            title="Create Squad"
                            aria-label={`Create squad for fixture ${item.id}`}
                          >
                            <CIcon icon={cilUserPlus} />
                          </CDropdownToggle>
                          <CDropdownMenu className="ge-row-actions-menu">
                            <CDropdownHeader>Create Squad</CDropdownHeader>
                            <CDropdownItem
                              href={`/manage-scores/team-players/${item.id}/${item.team}`}
                            >
                              <CIcon icon={cilUserPlus} className="me-2" />
                              {item?.team_name}
                            </CDropdownItem>
                            <CDropdownItem
                              href={`/manage-scores/team-players/${item.id}/${item.team2}`}
                            >
                              <CIcon icon={cilUserPlus} className="me-2" />
                              {item?.team2_name}
                            </CDropdownItem>
                          </CDropdownMenu>
                        </CDropdown>
                      )}

                      {isSingleTeamScoring ? (
                        <CButton
                          color="secondary"
                          variant="ghost"
                          size="sm"
                          className="p-1"
                          title="Manage Scores"
                          aria-label={`Manage scores for fixture ${item.id}`}
                          onClick={() => redirectPage(item.id, item.team)}
                        >
                          <CIcon icon={cilSpreadsheet} />
                        </CButton>
                      ) : (
                        <CDropdown alignment="end" placement="bottom-end" portal>
                          <CDropdownToggle
                            color="secondary"
                            variant="ghost"
                            size="sm"
                            caret={false}
                            className="p-1 ge-row-actions-toggle"
                            title="Manage Scores"
                            aria-label={`Manage scores for fixture ${item.id}`}
                          >
                            <CIcon icon={cilSpreadsheet} />
                          </CDropdownToggle>
                          <CDropdownMenu className="ge-row-actions-menu">
                            <CDropdownHeader>Manage Scores</CDropdownHeader>
                            <CDropdownItem href={`/team-scorecard/${item.id}/${item.team}`}>
                              <CIcon icon={cilSpreadsheet} className="me-2" />
                              {item?.team_name}
                            </CDropdownItem>
                            <CDropdownItem href={`/team-scorecard/${item.id}/${item.team2}`}>
                              <CIcon icon={cilSpreadsheet} className="me-2" />
                              {item?.team2_name}
                            </CDropdownItem>
                            <CDropdownItem href={`/full-scorecard/${item.id}`}>
                              <CIcon icon={cilDescription} className="me-2" />
                              Full Scorecard
                            </CDropdownItem>
                          </CDropdownMenu>
                        </CDropdown>
                      )}
                    </div>
                  </td>
                ),
              }}
              items={users?.data}
              itemsPerPage={itemsPerPage}
              itemsPerPageSelect
              loading={loading}
              pagination={{
                external: true,
              }}
              paginationProps={{
                activePage: activePage,
                pages: Math.ceil(users?.total / itemsPerPage) || 1,
              }}
              tableProps={{
                hover: true,
                responsive: true,
                className: 'align-middle mb-0',
              }}
              onActivePageChange={(activePage) => setActivePage(activePage)}
              onColumnFilterChange={(filter) => {
                setActivePage(1)
                setColumnFilter(filter)
              }}
              onItemsPerPageChange={(itemsPerPage) => {
                setActivePage(1)
                setItemsPerPage(itemsPerPage)
              }}
              onSorterChange={(sorter) => setColumnSorter(sorter)}
            />
          </div>
        </CCardBody>
      </CCard>

      {(gameType === 1 || gameType === 4) && (
        <AddForm
          visible={addFixtureVisible}
          onClose={() => setAddFixtureVisible(false)}
          gameType={gameType}
          activePage={activePage}
          columnFilter={columnFilter}
          columnSorter={columnSorter}
          itemsPerPage={itemsPerPage}
          fetchData={refetchFixtures}
        />
      )}
      {(gameType === 2 || gameType === 3) && (
        <AddFormForLeagueIndoorFixture
          visible={addFixtureVisible}
          onClose={() => setAddFixtureVisible(false)}
          activePage={activePage}
          columnFilter={columnFilter}
          columnSorter={columnSorter}
          itemsPerPage={itemsPerPage}
          fetchData={refetchFixtures}
        />
      )}

      <EditForm
        fixtureId={editFixtureId}
        visible={!!editFixtureId}
        onClose={() => setEditFixtureId(null)}
        gameType={gameType}
        activePage={activePage}
        columnFilter={columnFilter}
        columnSorter={columnSorter}
        itemsPerPage={itemsPerPage}
        fetchData={refetchFixtures}
      />

      <PreviewRoundTable
        visible={roundPreviewVisible}
        onClose={() => setRoundPreviewVisible(false)}
      />

      <FixtureSquadTable
        teamId={teamId}
        fixtureId={fixtureId}
        gameType={gameType}
        visible={showSquadModal}
        teamName={teamName}
        onClose={closeSquadModal}
      />
      <Notify
        setShowConfirm={setShowConfirm}
        showConfirm={showConfirm}
        setHandleNo={setHandleNo}
        handleNo={handleNo}
        handleYes={handleYes}
        setHandleYes={setHandleYes}
        handleConfirm={handleConfirm}
        handleCancel={handleCancel}
        text="Are you sure you want to delete this?"
      />
      <Bootbox
        show={showDeleteAllConfirm}
        type={'confirm'}
        message={`Are you sure you want to delete ${selectedCount} selected ${
          selectedCount === 1 ? 'fixture' : 'fixtures'
        }?`}
        onSuccess={handleDeleteAllConfirm}
        onCancel={handleDeleteAllCancel}
        onClose={handleDeleteAllCancel}
      />
    </>
  )
}

export default ManageFixture
